// Content reader — single chokepoint between curriculum data + consumers.
// Phase B §B4.
//
// Critical: when audience === "independent_practice", only items with
// launchStatus="launch_safe" AND expert_review_needed=false are
// returned. This is the safety filter that prevents un-reviewed content
// from reaching independent practice. Spec calls this out as a hard
// safety property. Phase G session screens MUST go through
// getContentForMicroSkill, never read content packs directly.

import type {
  ContentPackName,
  ContentForPack,
  AuthoredContentBase,
} from "../types/content";
import type { ContentAudience } from "../types/adaptationTypes";

import controlledSentences from "../data/content/controlledSentences.json";
import decodablePassages from "../data/content/decodablePassages.json";
import comprehensionQuestions from "../data/content/comprehensionQuestions.json";
import semanticRelations from "../data/content/semanticRelations.json";
import grammarItems from "../data/content/grammarItems.json";
import mathExemplars from "../data/content/mathExemplars.json";
import wordProblemTemplates from "../data/content/wordProblemTemplates.json";
import feedbackStrings from "../data/content/feedbackStrings.json";
import scaffoldPrompts from "../data/content/scaffoldPrompts.json";
import parentSummaryTemplates from "../data/content/parentSummaryTemplates.json";
import encodingWords from "../data/content/encodingWords.json";
import categories from "../data/content/categories.json";
import attributes from "../data/content/attributes.json";
import multipleMeaningWords from "../data/content/multipleMeaningWords.json";
import tier2Vocabulary from "../data/content/tier2Vocabulary.json";
import foundationItems from "../data/content/foundationItems.json";
import rhyming from "../data/content/rhyming.json";
import syllableCounting from "../data/content/syllable_counting.json";

import legacyMapRaw from "../data/manifests/legacySkillIdMap.json";

const PACK_DATA: Record<ContentPackName, AuthoredContentBase[]> = {
  controlledSentences: controlledSentences as unknown as AuthoredContentBase[],
  decodablePassages: decodablePassages as unknown as AuthoredContentBase[],
  comprehensionQuestions: comprehensionQuestions as unknown as AuthoredContentBase[],
  semanticRelations: semanticRelations as unknown as AuthoredContentBase[],
  grammarItems: grammarItems as unknown as AuthoredContentBase[],
  mathExemplars: mathExemplars as unknown as AuthoredContentBase[],
  wordProblemTemplates: wordProblemTemplates as unknown as AuthoredContentBase[],
  feedbackStrings: feedbackStrings as unknown as AuthoredContentBase[],
  scaffoldPrompts: scaffoldPrompts as unknown as AuthoredContentBase[],
  parentSummaryTemplates: parentSummaryTemplates as unknown as AuthoredContentBase[],
  encodingWords: encodingWords as unknown as AuthoredContentBase[],
  categories: categories as unknown as AuthoredContentBase[],
  attributes: attributes as unknown as AuthoredContentBase[],
  multipleMeaningWords: multipleMeaningWords as unknown as AuthoredContentBase[],
  tier2Vocabulary: tier2Vocabulary as unknown as AuthoredContentBase[],
  foundationItems: foundationItems as unknown as AuthoredContentBase[],
  rhyming: rhyming as unknown as AuthoredContentBase[],
  syllable_counting: syllableCounting as unknown as AuthoredContentBase[],
};

const legacyMap = legacyMapRaw as unknown as {
  legacy_to_microskill: Record<string, string>;
  microskill_to_legacy: Record<string, string[]>;
};

/** Return all items in a pack, unfiltered. Validators run separately. */
export function getContent<P extends ContentPackName>(pack: P): ContentForPack<P>[] {
  return PACK_DATA[pack] as unknown as ContentForPack<P>[];
}

interface GetForMicroSkillOpts {
  audience: ContentAudience;
  /** Caller asserts they will not surface this content for independent
   *  practice. Required when audience === "independent_practice" and
   *  the only available items are expert_review_needed — caller must
   *  fall back to safe review (spec non-negotiable). */
  fallbackPolicy?: "fail_closed" | "scaffolded_only" | "include_review_queue";
}

/** Resolve content for a (microSkillId, pack) pair with audience filter.
 *
 *   audience: "independent_practice"  → only launch_safe items returned. Fail-closed.
 *   audience: "scaffolded"            → launch_safe + expert_review_needed (scaffold is the safety surface).
 *   audience: "review_queue"          → all items.
 *
 * microSkillId may be the engine ms.* form or the legacy module id;
 * both resolve via legacySkillIdMap. Match is against each item's
 * `targetSkill` field (when present); packs without targetSkill
 * return all items in the pack subject to the audience filter.
 */
/** Resolve the matching set for (microSkillId, pack) BEFORE the audience
 *  filter — the "dedicated pool wins" logic shared by getContentForMicroSkill
 *  and (MA-663) getMergedContentForMicroSkill, which runs it once per pack
 *  in a skill's PACK_MERGE list. Pure, not exported — internal helper. */
function matchesForMicroSkill(microSkillId: string, pack: ContentPackName): AuthoredContentBase[] {
  const all = PACK_DATA[pack];

  // Build the set of acceptable targetSkill labels.
  const candidates = new Set<string>();
  candidates.add(microSkillId);
  if (legacyMap.legacy_to_microskill[microSkillId]) {
    candidates.add(legacyMap.legacy_to_microskill[microSkillId]); // legacy → ms
  }
  for (const legacy of legacyMap.microskill_to_legacy[microSkillId] ?? []) {
    candidates.add(legacy); // ms → legacy[]
  }

  // C-2: DEDICATED POOL WINS. If this microskill has its OWN tagged items in
  // the pack, serve ONLY those — never dilute them with the generic
  // null-targetSkill fallback. (ms.read.21 "Prefixes & suffixes" used to be
  // served the same generic grammar pool as ms.read.17 because every grammar
  // item was null-tagged, so a kid "mastered" prefixes without seeing one;
  // the morphology items are now tagged ms.read.21.) grammarItems is the ONLY
  // mixed (null + tagged) pack, so this is a no-op for every other skill/pack:
  // all-tagged packs already returned candidate matches, and all-null packs
  // still fall through to the generic pool below.
  const dedicated = all.filter((item) => {
    const ts = (item as { targetSkill?: string }).targetSkill;
    return ts != null && candidates.has(ts);
  });
  return dedicated.length > 0
    ? dedicated
    : all.filter((item) => {
        const ts = (item as { targetSkill?: string }).targetSkill;
        if (ts == null) return true; // generic fallback — pack/skill has no dedicated items
        return candidates.has(ts);
      });
}

export function getContentForMicroSkill<P extends ContentPackName>(
  microSkillId: string,
  pack: P,
  opts: GetForMicroSkillOpts,
): ContentForPack<P>[] {
  const matches = matchesForMicroSkill(microSkillId, pack);
  return applyAudienceFilter(matches, opts) as unknown as ContentForPack<P>[];
}

/** One record from a merged-pool pull, tagged with the pack its OWN data
 *  literally came from (never the skill's `primaryPackForSkill`). */
export interface MergedContentItem {
  pack: ContentPackName;
  item: AuthoredContentBase;
}

/** MA-663 — union content for a skill across MULTIPLE packs (PACK_MERGE),
 *  in the given pack order, each record tagged with ITS OWN source pack so
 *  a dispatcher (contentItemToEngineItem) can route each item to the right
 *  mapper without guessing from the skill id. Runs the SAME "dedicated pool
 *  wins" + audience-filter logic getContentForMicroSkill runs, once per
 *  pack, then concatenates — so a skill's merged pool is exactly what
 *  calling getContentForMicroSkill once per pack and concatenating in order
 *  would produce; this function exists so callers don't have to know that.
 *
 *  Cross-pack mapper-confusion guard: each returned item is verified (by
 *  reference) to be an actual member of PACK_DATA[pack] before being tagged
 *  — a foundationItems record can never be mistagged mathExemplars or vice
 *  versa, because the tag is assigned at the exact point the record is read
 *  out of that pack's own array, never derived from the skill id. */
export function getMergedContentForMicroSkill(
  microSkillId: string,
  packs: readonly ContentPackName[],
  opts: GetForMicroSkillOpts,
): MergedContentItem[] {
  const out: MergedContentItem[] = [];
  for (const pack of packs) {
    const matches = matchesForMicroSkill(microSkillId, pack);
    const filtered = applyAudienceFilter(matches, opts);
    for (const item of filtered) {
      if (!PACK_DATA[pack].includes(item)) {
        // Unreachable under this module's own construction (matches always
        // comes from PACK_DATA[pack] itself) — a defensive tripwire in case
        // a future refactor breaks that invariant and starts tagging a
        // record with a pack it didn't come from.
        throw new Error(
          `getMergedContentForMicroSkill: item ${String((item as { id?: string }).id)} tagged pack "${pack}" is not a member of PACK_DATA["${pack}"] — cross-pack mapper confusion.`,
        );
      }
      out.push({ pack, item });
    }
  }
  return out;
}

function applyAudienceFilter(
  items: AuthoredContentBase[],
  opts: GetForMicroSkillOpts,
): AuthoredContentBase[] {
  switch (opts.audience) {
    case "independent_practice":
      // Fail-closed: launch_safe only.
      return items.filter(
        (i) => i.launchStatus === "launch_safe" && i.expert_review_needed === false,
      );
    case "scaffolded":
      return items.filter(
        (i) => i.launchStatus === "launch_safe" || i.launchStatus === "expert_review_needed",
      );
    case "review_queue":
      return items;
    default: {
      // Exhaustiveness check — if a new ContentAudience is added, this
      // unreachable returns triggers a compile error if not handled.
      const _exhaustive: never = opts.audience;
      return _exhaustive;
    }
  }
}
